<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import CounterpartyMasterCard from '@/modules/counterparty/counterparty-master/components/CounterpartyMasterCard.vue';
import type { CounterpartyBankDetailsItem } from '@/modules/counterparty/counterparty-master/types/bank-details-form.ts';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const { item } = defineProps<{
  item: CounterpartyBankDetailsItem;
}>();

defineEmits<{
  edit: [];
  remove: [];
}>();

const noteRef = useTemplateRef('note');
const noteTooltipDisabled = ref(true);

const hasNote = computed(() => !!item.comment.trim());

function isClamped(el: HTMLElement | null) {
  return !!el && el.scrollHeight > el.clientHeight + 1;
}

function syncNoteTooltip() {
  noteTooltipDisabled.value = !isClamped(noteRef.value);
}

watch(
  () => item.comment,
  () => {
    void nextTick(syncNoteTooltip);
  }
);

onMounted(() => {
  void nextTick(syncNoteTooltip);
});
</script>

<template>
  <CounterpartyMasterCard
    class="bank-details-card"
    @edit="$emit('edit')"
    @remove="$emit('remove')"
  >
    <template #title>{{ item.bank.label }}</template>

    <div class="bank-body _flush">
      <p class="meta">{{ $t('counterparty.master.steps.bankDetails.field.bik') }}</p>
      <p class="value">{{ item.bankIdentificationCode }}</p>

      <p class="meta">{{ $t('counterparty.master.steps.bankDetails.field.correspondentAccount') }}</p>
      <p class="value">{{ item.correspondentAccount }}</p>

      <p class="meta">{{ $t('counterparty.master.steps.bankDetails.field.settlementAccount') }}</p>
      <p class="value">{{ item.currentAccount }}</p>

      <template v-if="hasNote">
        <p class="meta">{{ $t('counterparty.master.steps.bankDetails.card.note') }}</p>
        <UiTooltip
          class="clamp"
          :text="item.comment"
          :disabled="noteTooltipDisabled"
          placement="bottom"
          align="left"
          alt-boundary
          @pointerenter="syncNoteTooltip"
        >
          <p
            ref="note"
            class="value"
            title=""
          >
            {{ item.comment }}
          </p>
        </UiTooltip>
      </template>
    </div>
  </CounterpartyMasterCard>
</template>

<style scoped lang="scss">
.bank-details-card {
  min-height: calc(16px + var(--line-height-l) + 8px + 16px + var(--line-height-s) * 8);
}

.bank-body {
  min-width: 0;

  > :last-child {
    padding-right: 32px;
  }
}

.meta,
.value {
  margin: 0;
  min-width: 0;
  font-size: var(--font-size-s);
  line-height: var(--line-height-s);
}

.meta {
  color: var(--color-gray-500);
}

.clamp {
  display: block;
  min-width: 0;
}

.value {
  display: -webkit-box;
  overflow: hidden;
  color: var(--color-gray-900);
  overflow-wrap: anywhere;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  pointer-events: none;
}
</style>
